/**
 * Car profiles: everything the app knows about one car, as a file.
 *
 * Calibration takes driving to learn -- gear ratios need pull-aways and every
 * gear, the speed correction minutes of steady driving with GPS -- so it is
 * worth carrying: to another phone, across a reinstall, or back to a car
 * driven before. A profile holds the vehicle details and the learned
 * calibration, and the dashboards if asked.
 *
 * Nothing in a file is trusted: every part is checked and cleaned before it is
 * offered. And the driver picks which parts to apply, because they travel
 * differently -- gear ratios hold for any car of the same model, accelerator
 * calibration for the same car, the speed correction only for the same car on
 * the same tyres.
 *
 * Every learned part records the car it was learned on (by VIN, or by what the
 * car reports when it has no VIN), so an import can say when a part comes from
 * a different car than the one it is going into. It only warns: using a
 * sister car's gear ratios is a perfectly good idea.
 */
import {
  DEFAULT_VEHICLE, FUEL_CONSTANTS, GearEstimator, GpsTracker, LEARNED_PARTS, SIGNAL_BY_KEY, compareIdentities,
  normalizeVin, sanitizeIdentity, sanitizeLearned,
  type FuelType, type IdentityMatch, type LearnedPart, type LearnedState, type VehicleIdentity, type VehicleProfile,
} from '@obd/core';
import {
  STATE_VERSION, WIDGET_LABELS, defaultSettings, migrateState,
  type Dashboard, type Geom, type LearnedOwners, type PersistedState, type Widget, type WidgetType,
} from './appState';

export const CAR_PROFILE_FORMAT = 'dafer-obd2/car-profile';
export const CAR_PROFILE_VERSION = 1;

export interface CarProfile {
  format: typeof CAR_PROFILE_FORMAT;
  version: number;
  /** ISO timestamp. */
  exportedAt: string;
  /** The car the exporting device was used with. */
  car?: VehicleIdentity;
  /** The car each learned part was learned on, where known. */
  learnedFrom?: LearnedOwners;
  /** `car.vin`, repeated for the first version of the app, which reads only this. */
  vin?: string;
  vehicle: VehicleProfile;
  learned: LearnedState;
  /** Only when the dashboards were included; `schema` is the app state version they follow. */
  dashboards?: { schema: number; list: Dashboard[]; activeId: string };
}

export type ProfilePart = 'vehicle' | 'gears' | 'accelerator' | 'speed' | 'fuel' | 'dashboards';

export const PROFILE_PARTS: { part: ProfilePart; label: string; scope: string }[] = [
  { part: 'vehicle', label: 'Vehicle details', scope: 'Name, engine, gears, redline and tank, as entered in Setup.' },
  { part: 'gears', label: 'Gear ratios', scope: 'Right for any car of the same model and gearbox.' },
  { part: 'accelerator', label: 'Accelerator calibration', scope: 'Right for the same car.' },
  { part: 'speed', label: 'Speed correction', scope: 'Right for the same car on the same tyres.' },
  { part: 'fuel', label: 'Fuel correction', scope: 'Right for the same car and engine, from its fill-ups.' },
  { part: 'dashboards', label: 'Dashboards', scope: 'Replaces every dashboard on this device.' },
];

/** A file larger than this is not a profile. Real ones are a few kilobytes. */
export const MAX_PROFILE_BYTES = 2_000_000;

// ------------------------------------------------------------------- export

export function buildCarProfile(
  input: {
    vehicle: VehicleProfile;
    learned: LearnedState;
    car?: VehicleIdentity;
    learnedFrom?: LearnedOwners;
    dashboards?: { list: Dashboard[]; activeId: string };
  },
  now = new Date(),
): CarProfile {
  const profile: CarProfile = {
    format: CAR_PROFILE_FORMAT,
    version: CAR_PROFILE_VERSION,
    exportedAt: now.toISOString(),
    vehicle: { ...input.vehicle },
    learned: sanitizeLearned(input.learned),
  };
  const car = sanitizeIdentity(input.car);
  if (car) {
    profile.car = car;
    if (car.vin) profile.vin = car.vin;
  }
  // Owners only for parts that hold something.
  const owners: LearnedOwners = {};
  for (const part of LEARNED_PARTS) {
    const owner = sanitizeIdentity(input.learnedFrom?.[part]);
    if (owner && hasLearned(profile.learned, part)) owners[part] = owner;
  }
  if (Object.keys(owners).length > 0) profile.learnedFrom = owners;
  if (input.dashboards) {
    profile.dashboards = { schema: STATE_VERSION, list: input.dashboards.list, activeId: input.dashboards.activeId };
  }
  return profile;
}

export function serializeProfile(profile: CarProfile): string {
  return JSON.stringify(profile, null, 2) + '\n';
}

/** e.g. `citroen-c3-2026-09-16.json`. */
export function profileFileName(profile: CarProfile): string {
  const slug = profile.vehicle.name
    // Split accented letters and drop the accents, so "Citroën" becomes "citroen".
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return (slug || 'car') + '-' + profile.exportedAt.slice(0, 10) + '.json';
}

// ------------------------------------------------------------------- import

export type ParsedProfile =
  | { ok: true; profile: CarProfile; available: ProfilePart[]; warnings: string[] }
  | { ok: false; error: string };

/**
 * Read a profile file, cleaning every part. Parts that are missing or
 * unusable are left out of `available`; anything dropped along the way is
 * explained in `warnings`. `base` fills vehicle fields the file does not have.
 */
export function parseCarProfile(text: string, base: VehicleProfile = DEFAULT_VEHICLE): ParsedProfile {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: 'This file is not a car profile: it is not valid JSON.' };
  }
  if (!isRecord(raw) || raw.format !== CAR_PROFILE_FORMAT) {
    return { ok: false, error: 'This file is not a car profile saved by this app.' };
  }
  if (typeof raw.version !== 'number' || raw.version > CAR_PROFILE_VERSION) {
    return { ok: false, error: 'This profile was saved by a newer version of the app. Update the app and try again.' };
  }

  const warnings: string[] = [];
  const vehicle = sanitizeVehicle(raw.vehicle, base, warnings);
  const learned = sanitizeLearned(isRecord(raw.learned) ? (raw.learned as LearnedState) : undefined);
  const dashboards = sanitizeDashboards(raw.dashboards, warnings);
  const exportedAt = typeof raw.exportedAt === 'string' && !Number.isNaN(Date.parse(raw.exportedAt))
    ? raw.exportedAt
    : new Date(0).toISOString();

  const profile: CarProfile = {
    format: CAR_PROFILE_FORMAT, version: CAR_PROFILE_VERSION, exportedAt,
    vehicle: vehicle ?? { ...base }, learned,
  };
  const vin = normalizeVin(raw.vin);
  const car = sanitizeIdentity(raw.car) ?? (vin ? { vin } : undefined);
  if (car) {
    profile.car = car;
    if (car.vin) profile.vin = car.vin;
  }
  // Files from the first version name only one car, which then owns everything.
  const rawOwners = isRecord(raw.learnedFrom) ? raw.learnedFrom : undefined;
  const owners: LearnedOwners = {};
  for (const part of LEARNED_PARTS) {
    const owner = rawOwners ? sanitizeIdentity(rawOwners[part]) : car;
    if (owner && hasLearned(learned, part)) owners[part] = owner;
  }
  if (Object.keys(owners).length > 0) profile.learnedFrom = owners;
  if (dashboards) profile.dashboards = dashboards;

  const available: ProfilePart[] = [];
  if (vehicle) available.push('vehicle');
  for (const part of LEARNED_PARTS) if (hasLearned(learned, part)) available.push(part);
  if (dashboards) available.push('dashboards');
  if (available.length === 0) return { ok: false, error: 'This car profile has nothing in it that can be used.' };
  return { ok: true, profile, available, warnings };
}

export interface ProfileCheck {
  /** The profile's car against this device's car. */
  car: IdentityMatch;
  /** Each part that belongs to a particular car, against this device's car. */
  parts: Partial<Record<ProfilePart, IdentityMatch>>;
}

/**
 * Compare what a profile says about where it came from with the car this
 * device is used with. Only ever used to warn.
 */
export function checkProfile(profile: CarProfile, thisCar: VehicleIdentity | undefined): ProfileCheck {
  const parts: ProfileCheck['parts'] = {};
  const car = compareIdentities(profile.car, thisCar);
  parts.vehicle = car;
  for (const part of LEARNED_PARTS) {
    if (!hasLearned(profile.learned, part)) continue;
    parts[part] = compareIdentities(profile.learnedFrom?.[part] ?? profile.car, thisCar);
  }
  return { car, parts };
}

export function hasLearned(learned: LearnedState | undefined, part: LearnedPart): boolean {
  if (!learned) return false;
  if (part === 'speed') return (learned.speed?.seconds ?? 0) > 0;
  if (part === 'fuel') return learned.fuel !== undefined;
  return (learned[part]?.length ?? 0) > 0;
}

/**
 * The state and learned calibration that result from applying the chosen
 * parts of a profile. Pure: the caller stores the state, hands the vehicle to
 * the session, and replaces the learned calibration if one is returned.
 */
export function applyCarProfile(
  state: PersistedState,
  currentLearned: LearnedState,
  profile: CarProfile,
  parts: ReadonlySet<ProfilePart>,
): { state: PersistedState; learned?: LearnedState } {
  let next = state;
  if (parts.has('vehicle')) {
    // Figures chosen on purpose: fuel-type detection must not reseed them.
    const vehicle: VehicleProfile = { ...profile.vehicle, autoTuned: true };
    next = { ...next, settings: { ...next.settings, vehicle } };
  }
  if (parts.has('dashboards') && profile.dashboards) {
    next = { ...next, dashboards: profile.dashboards.list, activeDashboardId: profile.dashboards.activeId };
  }
  if (!LEARNED_PARTS.some((p) => parts.has(p))) return { state: next };

  const learned: LearnedState = { ...currentLearned };
  // Imported calibration still belongs to the car it was learned on.
  const owners: LearnedOwners = { ...next.learnedFrom };
  for (const part of LEARNED_PARTS) {
    if (!parts.has(part)) continue;
    if (part === 'gears') learned.gears = profile.learned.gears ?? [];
    else if (part === 'accelerator') learned.accelerator = profile.learned.accelerator ?? [];
    else if (part === 'fuel') learned.fuel = profile.learned.fuel;
    else learned.speed = profile.learned.speed ?? { gps: 0, obd: 0, seconds: 0 };
    const owner = profile.learnedFrom?.[part] ?? profile.car;
    if (owner) owners[part] = owner;
    else delete owners[part];
  }
  return { state: { ...next, learnedFrom: owners }, learned };
}

/** One line per part, for showing what a profile holds before applying it. */
export function describeProfile(profile: CarProfile): Record<ProfilePart, string> {
  const v = profile.vehicle;
  const learned = profile.learned;

  const estimator = new GearEstimator(v.gearCount);
  estimator.import(learned.gears);
  const gears = estimator.gears();
  const gearText = gears.length === 0
    ? 'None learned.'
    : gears.map((g) => (estimator.numberingIsKnown ? ordinal(g.gear) : '?') + ' ' + g.ratio.toFixed(1)).join(', ')
      + ' rpm per km/h' + (estimator.numberingIsKnown ? '.' : ', not numbered yet.');

  const pedals = learned.accelerator ?? [];
  const pedalText = pedals.length === 0
    ? 'None learned.'
    : pedals.map((p) => {
      const name = SIGNAL_BY_KEY.get(p.source)?.name ?? p.source;
      const rest = p.pinnedRest ?? p.rest;
      const top = p.pinnedFull ?? p.floor;
      const floored = top > rest + 5 ? ', floored at ' + top.toFixed(1) + '%' : '';
      return name + ': released at ' + rest.toFixed(1) + '%' + floored + '.';
    }).join(' ');

  const tracker = new GpsTracker();
  tracker.importCalibration(learned.speed);
  const factor = tracker.factor;
  const seconds = tracker.calibrationSeconds;
  const speedText = factor !== undefined
    ? 'Speed ' + correctionText(factor) + ', from ' + formatSeconds(seconds) + ' of driving.'
    : seconds > 0
      ? 'Not enough data yet (' + Math.round(seconds) + ' of 30 seconds).'
      : 'None learned.';

  const fuel = learned.fuel;
  const fuelText = fuel
    ? 'Fuel estimate ' + correctionText(fuel.factor, 'the pump') + ', from ' + fuel.spans
      + (fuel.spans === 1 ? ' fill-up span' : ' fill-up spans') + ' over ' + Math.round(fuel.distanceKm) + ' km.'
    : 'None learned.';

  const dashboards = profile.dashboards?.list ?? [];
  return {
    vehicle: v.name + ' — ' + v.fuelType + ', ' + v.displacementL + ' L, ' + v.cylinders + ' cylinders, '
      + v.gearCount + ' gears, redline ' + v.redlineRpm + ' rpm.',
    gears: gearText,
    accelerator: pedalText,
    speed: speedText,
    fuel: fuelText,
    dashboards: dashboards.length === 0
      ? 'Not included.'
      : dashboards.length + ': ' + dashboards.map((d) => d.name).join(', ') + '.',
  };
}

// ---------------------------------------------------------------- cleaning

type NumericField = {
  [K in keyof VehicleProfile]: VehicleProfile[K] extends number ? K : never;
}[keyof VehicleProfile];

/** Plausible ranges; anything outside is a corrupt file, not an unusual car. */
const VEHICLE_NUMBERS: { key: NumericField; label: string; min: number; max: number; integer?: boolean }[] = [
  { key: 'displacementL', label: 'displacement', min: 0.1, max: 20 },
  { key: 'volumetricEfficiency', label: 'volumetric efficiency', min: 0.2, max: 1.5 },
  { key: 'cylinders', label: 'cylinders', min: 1, max: 16, integer: true },
  { key: 'massKg', label: 'mass', min: 100, max: 50_000 },
  { key: 'gearCount', label: 'forward gears', min: 1, max: 12, integer: true },
  { key: 'redlineRpm', label: 'redline', min: 1000, max: 20_000 },
  { key: 'ecoShiftRpm', label: 'economy shift point', min: 500, max: 20_000 },
  { key: 'tankCapacityL', label: 'tank', min: 1, max: 2000 },
];

function sanitizeVehicle(raw: unknown, base: VehicleProfile, warnings: string[]): VehicleProfile | undefined {
  if (!isRecord(raw)) return undefined;
  const v: VehicleProfile = { ...DEFAULT_VEHICLE, ...base };
  const ignored: string[] = [];

  if (typeof raw.name === 'string' && raw.name.trim()) v.name = raw.name.trim().slice(0, 60);
  if (typeof raw.fuelType === 'string' && Object.hasOwn(FUEL_CONSTANTS, raw.fuelType)) {
    v.fuelType = raw.fuelType as FuelType;
  } else if (raw.fuelType !== undefined) {
    ignored.push('fuel type');
  }
  if (typeof raw.fuelTypeLocked === 'boolean') v.fuelTypeLocked = raw.fuelTypeLocked;

  for (const { key, label, min, max, integer } of VEHICLE_NUMBERS) {
    const value = raw[key];
    if (value === undefined) continue;
    if (isNum(value) && value >= min && value <= max && (!integer || Number.isInteger(value))) {
      v[key] = value;
    } else {
      ignored.push(label);
    }
  }
  if (ignored.length > 0) {
    warnings.push('Vehicle details that were not believable were left as they are: ' + ignored.join(', ') + '.');
  }
  return v;
}

const WIDGET_TYPES = new Set(Object.keys(WIDGET_LABELS));

function sanitizeDashboards(raw: unknown, warnings: string[]): CarProfile['dashboards'] | undefined {
  if (raw === undefined) return undefined;
  const unreadable = 'The dashboards in this file could not be read and were left out.';
  if (!isRecord(raw) || !Array.isArray(raw.list) || typeof raw.schema !== 'number') {
    warnings.push(unreadable);
    return undefined;
  }

  let droppedWidgets = 0;
  const seen = new Set<string>();
  const list: Dashboard[] = [];
  for (const d of raw.list) {
    if (!isRecord(d) || typeof d.id !== 'string' || seen.has(d.id) || !Array.isArray(d.widgets)) continue;
    seen.add(d.id);
    const widgets = d.widgets.map(sanitizeWidget).filter((w): w is Widget => w !== undefined);
    droppedWidgets += d.widgets.length - widgets.length;
    const board: Dashboard = {
      id: d.id,
      name: typeof d.name === 'string' && d.name.trim() ? d.name.slice(0, 60) : 'Dashboard',
      columns: isInt(d.columns, 1, 24) ? d.columns : 12,
      widgets,
    };
    if (typeof d.fit === 'boolean') board.fit = d.fit;
    const portrait = sanitizePortrait(d.portrait);
    if (portrait) board.portrait = portrait;
    list.push(board);
  }
  if (list.length === 0) {
    warnings.push(unreadable);
    return undefined;
  }

  // Saved by an older version: migrate them the same way stored state is.
  const migrated = migrateState({
    version: raw.schema, dashboards: list, activeDashboardId: '', settings: defaultSettings(),
  });
  if (!migrated) {
    warnings.push('The dashboards in this file are from a version of the app this one does not know, and were left out.');
    return undefined;
  }
  if (droppedWidgets > 0) {
    warnings.push(
      droppedWidgets + ' dashboard widget' + (droppedWidgets === 1 ? '' : 's') + ' could not be read and were left out.',
    );
  }
  const activeId = typeof raw.activeId === 'string' && seen.has(raw.activeId) ? raw.activeId : list[0]?.id ?? '';
  return { schema: STATE_VERSION, list: migrated.dashboards, activeId };
}

function sanitizeWidget(raw: unknown): Widget | undefined {
  if (!isRecord(raw)) return undefined;
  const { id, type, signals } = raw;
  if (typeof id !== 'string' || typeof type !== 'string' || !WIDGET_TYPES.has(type)) return undefined;
  if (!Array.isArray(signals) || signals.length === 0 || !signals.every((k) => typeof k === 'string')) return undefined;
  const geom = sanitizeGeom(raw);
  if (!geom) return undefined;
  const w: Widget = { id, type: type as WidgetType, signals: signals as string[], ...geom };
  if (typeof raw.title === 'string') w.title = raw.title.slice(0, 60);
  if (isNum(raw.min)) w.min = raw.min;
  if (isNum(raw.max)) w.max = raw.max;
  if (isNum(raw.intervalMs) && raw.intervalMs > 0) w.intervalMs = raw.intervalMs;
  if (isNum(raw.windowS) && raw.windowS > 0) w.windowS = raw.windowS;
  if (typeof raw.sparkline === 'boolean') w.sparkline = raw.sparkline;
  return w;
}

function sanitizeGeom(raw: Record<string, unknown>): Geom | undefined {
  const { x, y, w, h } = raw;
  if (!isInt(x, 0, 1000) || !isInt(y, 0, 1000) || !isInt(w, 1, 24) || !isInt(h, 1, 1000)) return undefined;
  return { x, y, w, h };
}

function sanitizePortrait(raw: unknown): Record<string, Geom> | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, Geom> = {};
  for (const [id, g] of Object.entries(raw)) {
    const geom = isRecord(g) ? sanitizeGeom(g) : undefined;
    if (geom) out[id] = geom;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// ----------------------------------------------------------------- helpers

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown, min: number, max: number): v is number =>
  isNum(v) && Number.isInteger(v) && v >= min && v <= max;

/** "reads 3.4% high", for a factor that scales the car's reading to ground speed. */
/**
 * How far a reading is from the truth, given the factor that corrects it:
 * true value = factor x reading. Over 1 means the reading is low.
 */
export function correctionText(factor: number, against = 'GPS'): string {
  const error = (1 / factor - 1) * 100;
  if (Math.abs(error) < 0.3) return 'matches ' + against;
  return 'reads ' + Math.abs(error).toFixed(1) + '% ' + (error > 0 ? 'high' : 'low');
}

export function formatSeconds(seconds: number): string {
  if (seconds < 90) return Math.round(seconds) + ' s';
  if (seconds < 5400) return Math.round(seconds / 60) + ' min';
  return (seconds / 3600).toFixed(1) + ' h';
}

export function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return n + suffix;
}
